import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { RefreshCw, Upload, AlertTriangle, CheckCircle, Loader2, History, Trash2, AlertCircle } from 'lucide-react';

const fmtZAR = (v) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(Number(v || 0));

/**
 * ClientBankReconciliationTab
 * ----------------------------
 * Admin controls for the client-side (loan_book) bank reconciliation
 * subsystem — see backend/services/client_bank_reconciliation.py. Kept
 * deliberately separate from the investor-side Investec tools on this
 * page: this only ever touches the ONE "main_disbursement" Investec
 * account, matching real client repayments/drawdowns against it.
 *
 * Matching is automatic when exactly one bank transaction fits an
 * amount+date; when a repayment amount matches MORE than one bank
 * transaction, nobody auto-picks — an admin confirms which one it
 * actually is here (client repayment references rarely carry a usable
 * client/loan code, so ambiguity is expected and must never be guessed).
 */
export function ClientBankReconciliationTab({ API_URL, headers }) {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [syncing, setSyncing] = useState(false);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      setLoadingPending(true);
      const r = await axios.get(`${API_URL}/api/admin/client-bank/pending-matches`, { headers });
      setPending(r.data?.pending || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pending matches');
    } finally {
      setLoadingPending(false);
    }
  }, [API_URL, headers]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const fetchBatches = useCallback(async () => {
    try {
      setLoadingBatches(true);
      const r = await axios.get(`${API_URL}/api/admin/client-bank/historical-batches`, { headers });
      setBatches(r.data?.batches || []);
    } catch (err) {
      // Silent - endpoint may not be deployed yet
    } finally {
      setLoadingBatches(false);
    }
  }, [API_URL, headers]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const r = await axios.post(
        `${API_URL}/api/admin/client-bank/sync`,
        null,
        { headers, params: { from_date: fromDate, to_date: toDate } },
      );
      toast.success(`Synced: ${r.data.fetched} fetched, ${r.data.inserted} new, ${r.data.skipped_duplicate} already known`);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Choose a CSV file first');
      return;
    }
    try {
      setUploading(true);
      setUploadResult(null);
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API_URL}/api/admin/client-bank/import-historical-csv`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const errCount = (r.data?.errors || []).length;
      setUploadResult(r.data);
      toast.success(`Imported ${r.data.imported} row(s), ${r.data.skipped_duplicate} already known` + (errCount ? `, ${errCount} row error(s)` : ''));
      setFile(null);
      fetchPending();
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Historical import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    const msg = `Delete historical import batch?\n\n` +
      `• ${batch.row_count} bank transactions\n` +
      `• Date range: ${(batch.min_date || '').slice(0, 10)} → ${(batch.max_date || '').slice(0, 10)}\n` +
      (batch.filename ? `• File: ${batch.filename}\n` : '') +
      `\nAny repayment currently matched to a row in this batch will be released back to unmatched. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      setDeletingBatchId(batch.batch_id);
      const r = await axios.delete(
        `${API_URL}/api/admin/client-bank/historical-batches/${encodeURIComponent(batch.batch_id)}`,
        { headers },
      );
      toast.success(`Deleted ${r.data.deleted_count} row(s)`);
      fetchBatches();
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingBatchId(null);
    }
  };

  const handleConfirm = async (batchId, bankTransactionId) => {
    try {
      setConfirmingId(bankTransactionId);
      await axios.post(
        `${API_URL}/api/admin/client-bank/confirm-match`,
        { batch_id: batchId, bank_transaction_id: bankTransactionId },
        { headers },
      );
      toast.success('Match confirmed');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm match');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="client-bank-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sync Main Account</CardTitle>
          <CardDescription>
            Pulls real transactions from the fund's Main Account (Investec) so client repayments/drawdowns can be matched against them.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label>From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="client-bank-sync-from" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="client-bank-sync-to" />
          </div>
          <Button onClick={handleSync} disabled={syncing} data-testid="client-bank-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historical Backfill</CardTitle>
          <CardDescription>
            Upload the Main Account's transaction history for periods pre-dating live sync. Columns: transaction_date, amount (signed), description — optional investec_uuid for dedup.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm"
            data-testid="client-bank-historical-file"
          />
          <Button onClick={handleUpload} disabled={uploading || !file} data-testid="client-bank-historical-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </CardContent>
        {uploadResult && (
          <CardContent className="pt-0">
            <div className={`p-3 rounded border ${(uploadResult.errors || []).length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'} space-y-2 text-sm`} data-testid="client-bank-historical-result">
              <p className="font-semibold flex items-center gap-1">
                {(uploadResult.errors || []).length > 0
                  ? <AlertCircle className="h-4 w-4 text-amber-600" />
                  : <CheckCircle className="h-4 w-4 text-emerald-600" />}
                Upload complete
              </p>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-emerald-700">{uploadResult.imported}</p>
                  <p className="text-slate-500">Imported</p>
                </div>
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-slate-700">{uploadResult.skipped_duplicate}</p>
                  <p className="text-slate-500">Duplicates skipped</p>
                </div>
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-rose-700">{(uploadResult.errors || []).length}</p>
                  <p className="text-slate-500">Row errors</p>
                </div>
              </div>
              {(uploadResult.errors || []).length > 0 && (
                <div className="text-xs space-y-0.5">
                  <p className="font-semibold text-amber-900">Errors (first 10):</p>
                  {uploadResult.errors.slice(0, 10).map((e, i) => (
                    <p key={i} className="text-amber-800">{e}</p>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" /> Import History
              </CardTitle>
              <CardDescription>Every historical CSV upload batch. Delete a batch to surgically undo it.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchBatches} disabled={loadingBatches} data-testid="client-bank-batches-refresh">
              <RefreshCw className={`h-3 w-3 mr-1 ${loadingBatches ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No historical uploads yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead>Date range</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b, i) => (
                  <TableRow key={b.batch_id} data-testid={`client-bank-batch-row-${i}`}>
                    <TableCell className="text-xs">
                      <div className="truncate max-w-[220px]">{b.filename || <span className="italic text-slate-400">legacy import (no filename recorded)</span>}</div>
                      <div className="text-[10px] text-slate-400 font-mono truncate max-w-[220px]">{b.batch_id?.slice(0, 8)}…{b.uploaded_by ? ` · ${b.uploaded_by}` : ''}</div>
                    </TableCell>
                    <TableCell className="text-right"><Badge variant="outline">{b.row_count}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-600">{(b.min_date || '').slice(0, 10)} → {(b.max_date || '').slice(0, 10)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString('en-ZA') : '—'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteBatch(b)}
                        disabled={deletingBatchId === b.batch_id}
                        data-testid={`client-bank-batch-delete-${i}`}
                      >
                        {deletingBatchId === b.batch_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Pending Match Confirmations {pending.length > 0 && `(${pending.length})`}
          </CardTitle>
          <CardDescription>
            A repayment's amount matched more than one bank transaction — client references rarely carry a usable identifier, so these are never auto-picked. Confirm which one is correct.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPending && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loadingPending && pending.length === 0 && (
            <div className="text-sm text-slate-500">Nothing awaiting confirmation.</div>
          )}
          {!loadingPending && pending.map((batch) => (
            <div key={batch.batch_id} className="border rounded-md p-3 mb-3" data-testid={`pending-batch-${batch.batch_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm">
                  <span className="font-medium">{fmtZAR(batch.total_amount)}</span>
                  <span className="text-slate-500 ml-2">{batch.repayment_date}</span>
                  <span className="text-slate-500 ml-2">Client: {batch.client_id}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {batch.loans.length} loan(s): {batch.loans.map((l) => l.loan_id).join(', ')}
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batch.candidates.map((c) => (
                    <TableRow key={c.id} data-testid={`pending-candidate-${c.id}`}>
                      <TableCell className="font-mono text-xs">{c.transaction_date}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtZAR(c.amount)}</TableCell>
                      <TableCell className="text-xs max-w-[300px] truncate" title={c.description}>{c.description}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => handleConfirm(batch.batch_id, c.id)}
                          disabled={confirmingId === c.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-match-${c.id}`}
                        >
                          {confirmingId === c.id ? '…' : <><CheckCircle className="h-3 w-3 mr-1" />This one</>}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
